import React from 'react';
import {
    Alert, Autocomplete, Box, Button, CircularProgress, Collapse,
    Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, IconButton, TextField,
    ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/sv';
import 'dayjs/locale/es';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useTranslation } from 'react-i18next';
import { fetchAuthSession } from 'aws-amplify/auth';
import { list } from 'aws-amplify/storage';
import S3Image from './S3Image';
import { getDeviceSchedule, setDeviceSchedule, deleteDeviceSchedule } from '../utils/clientUtils';

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_IDX = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };

const TIMEZONES = (() => {
    try { return Intl.supportedValuesOf('timeZone'); }
    catch { return ['UTC', 'Europe/Vienna', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles']; }
})();

function uses12h(lang) {
    try {
        const sample = new Intl.DateTimeFormat(lang, { hour: 'numeric' }).format(new Date(2000, 0, 1, 13));
        return /am|pm/i.test(sample);
    } catch {
        return lang === 'en';
    }
}

function timeStrToDayjs(hhmm) {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    return dayjs().hour(h).minute(m).second(0);
}

function dayjsToTimeStr(dj) {
    if (!dj || !dj.isValid()) return '00:00';
    return `${String(dj.hour()).padStart(2, '0')}:${String(dj.minute()).padStart(2, '0')}`;
}

function makeId() {
    return Math.random().toString(36).slice(2, 10);
}

function makeDefaultEvent() {
    return {
        id: makeId(),
        type: 'weekly',
        time: '08:00',
        days_of_week: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
        date: null,
        light_1: null,
        light_2: null,
        display: null,
        images: {},
    };
}

function defaultTZ() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
    catch { return 'UTC'; }
}

function scheduleToInternal(s) {
    if (!s) return { timezone: defaultTZ(), events: [] };
    return {
        timezone: s.timezone || defaultTZ(),
        events: (s.events || []).map(e => ({ id: makeId(), ...e })),
    };
}

function nextOccurrenceMs(event) {
    const [h, m] = (event.time || '00:00').split(':').map(Number);
    if (event.type === 'one_time') {
        if (!event.date) return Infinity;
        return new Date(
            event.date + 'T' + String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0') + ':00'
        ).getTime();
    }
    const days = (event.days_of_week || []).map(d => DAY_IDX[d]).filter(d => d !== undefined);
    if (!days.length) return Infinity;
    const now = new Date();
    const curDay = now.getDay();
    const curMins = now.getHours() * 60 + now.getMinutes();
    const evtMins = h * 60 + m;
    let minOffset = 8;
    for (const d of days) {
        let offset = (d - curDay + 7) % 7;
        if (offset === 0 && evtMins <= curMins) offset = 7;
        if (offset < minOffset) minOffset = offset;
    }
    return now.getTime() + minOffset * 86400000 + (evtMins - curMins) * 60000;
}

function TriState({ value, label, onChange }) {
    const { t } = useTranslation();
    const strVal = value === null ? 'null' : String(value);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ minWidth: 60 }}>{label}</Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={strVal}
                onChange={(_, v) => {
                    if (v === null) return;
                    onChange(v === 'null' ? null : v === 'true');
                }}
            >
                <ToggleButton value="null" sx={{ minWidth: 36 }}>—</ToggleButton>
                <ToggleButton value="true" sx={{
                    minWidth: 36,
                    '&.Mui-selected': {
                        backgroundColor: 'success.main',
                        color: 'success.contrastText',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: 'success.dark' },
                    },
                }}>{t('deviceSchedule.on')}</ToggleButton>
                <ToggleButton value="false" sx={{
                    minWidth: 36,
                    '&.Mui-selected': {
                        backgroundColor: 'warning.main',
                        color: 'warning.contrastText',
                        fontWeight: 700,
                        '&:hover': { backgroundColor: 'warning.dark' },
                    },
                }}>{t('deviceSchedule.off')}</ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );
}

function BoardImagePicker({ company, deviceTypeID, boardID, boardHeight, boardWidth, value, onChange, compact = false }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [images, setImages] = React.useState([]);
    const [tick, setTick] = React.useState(0);

    React.useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    async function fetchImages() {
        try {
            const result = await list({
                path: `gtms4/company=${company}/device=${deviceTypeID}/board=${boardID}/`,
                options: { listAll: true, bucket: 'upload' },
            });
            const items = (result.items || [])
                .filter(img => img.path && img.path.split('/').pop())
                .sort((a, b) => a.path.split('/').pop().localeCompare(b.path.split('/').pop()));
            setImages(items);
        } catch {
            setImages([]);
        }
    }

    function handleOpen() {
        setOpen(true);
        fetchImages();
    }

    const basename = value ? value.split('/').pop() : null;
    const thumbW = boardWidth || boardHeight;

    return (
        <>
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                onClick={handleOpen}
            >
                {basename ? (
                    <>
                        <S3Image
                            src={value}
                            boardHeight={boardHeight}
                            sx={{ height: boardHeight, width: thumbW, borderRadius: 0.5, flexShrink: 0 }}
                            tick={tick}
                        />
                        {!compact && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{basename}</Typography>
                        )}
                    </>
                ) : (
                    <Button size="small" variant="outlined" component="span">
                        {t('deviceSchedule.noImage')}
                    </Button>
                )}
            </Box>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{t('deviceSchedule.images')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Box
                            sx={{
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                height: boardHeight,
                                width: thumbW,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                flexShrink: 0,
                                boxSizing: 'border-box',
                            }}
                            onClick={() => { onChange(null); setOpen(false); }}
                        >
                            <Typography variant="caption">—</Typography>
                        </Box>
                        {images.map(img => (
                            <Box
                                key={img.path}
                                sx={{
                                    cursor: 'pointer',
                                    outline: img.path === value ? '2px solid' : 'none',
                                    outlineColor: 'primary.main',
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    height: boardHeight,
                                    width: thumbW,
                                    overflow: 'hidden',
                                    display: 'flex',
                                }}
                                onClick={() => { onChange(img.path); setOpen(false); }}
                            >
                                <S3Image src={img.path} boardHeight={boardHeight} sx={{ height: boardHeight, width: thumbW, minWidth: thumbW }} tick={tick} />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const DAY_TOGGLE_SX = {
    px: 1,
    minWidth: 40,
    fontWeight: 500,
    color: 'text.secondary',
    borderColor: 'divider',
    '&.Mui-selected': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        fontWeight: 700,
        '&:hover': { backgroundColor: 'primary.dark', color: 'primary.contrastText' },
    },
};

function EventEditor({ event, boards, company, deviceTypeID, ampm, onChange, onDelete, compact = false }) {
    const { t } = useTranslation();

    function update(patch) {
        onChange({ ...event, ...patch });
    }

    const boardPickers = boards.length > 0 && (
        <Box>
            <Typography variant="caption" sx={{ display: compact ? 'none' : 'block', mb: 0.5 }}>
                {t('deviceSchedule.images')}
            </Typography>
            {boards.map(board => (
                <Box key={board.position} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ minWidth: 60 }}>
                        {t('deviceSchedule.board', { n: board.position + 1 })}:
                    </Typography>
                    <BoardImagePicker
                        company={company}
                        deviceTypeID={deviceTypeID}
                        boardID={board.position}
                        boardHeight={board.height || 60}
                        boardWidth={board.width || null}
                        compact={compact}
                        value={event.images?.[String(board.position)] ?? null}
                        onChange={path => {
                            const images = { ...event.images, [String(board.position)]: path };
                            update({ images });
                        }}
                    />
                </Box>
            ))}
        </Box>
    );

    const triStates = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TriState value={event.light_1} label={t('deviceSchedule.light1')} onChange={v => update({ light_1: v })} />
            <TriState value={event.light_2} label={t('deviceSchedule.light2')} onChange={v => update({ light_2: v })} />
            <TriState value={event.display} label={t('deviceSchedule.display')} onChange={v => update({ display: v })} />
        </Box>
    );

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Row 1: type toggle + delete */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={event.type}
                    onChange={(_, v) => { if (v) update({ type: v, date: null }); }}
                >
                    <ToggleButton value="weekly">{t('deviceSchedule.typeWeekly')}</ToggleButton>
                    <ToggleButton value="one_time">{t('deviceSchedule.typeOneTime')}</ToggleButton>
                </ToggleButtonGroup>
                <IconButton size="small" color="error" onClick={onDelete} sx={{ ml: 'auto' }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            {compact ? (
                /* Compact: time + days/date on one line */
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TimePicker
                        label={t('deviceSchedule.time')}
                        value={timeStrToDayjs(event.time)}
                        onChange={v => update({ time: dayjsToTimeStr(v) })}
                        ampm={ampm}
                        slotProps={{ textField: { size: 'small' } }}
                        sx={{ width: 140, flexShrink: 0 }}
                    />
                    {event.type === 'weekly' ? (
                        <ToggleButtonGroup
                            value={event.days_of_week}
                            onChange={(_, val) => update({ days_of_week: val })}
                            size="small"
                            sx={{ flexWrap: 'wrap', alignContent: 'flex-start' }}
                        >
                            {DAYS.map(d => (
                                <ToggleButton key={d} value={d} sx={DAY_TOGGLE_SX}>
                                    {t(`deviceSchedule.days.${d}`)}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ) : (
                        <DatePicker
                            label={t('deviceSchedule.date')}
                            value={event.date ? dayjs(event.date) : null}
                            onChange={v => update({ date: v ? v.format('YYYY-MM-DD') : null })}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    )}
                </Box>
            ) : (
                /* Normal: time then days/date stacked */
                <>
                    <TimePicker
                        label={t('deviceSchedule.time')}
                        value={timeStrToDayjs(event.time)}
                        onChange={v => update({ time: dayjsToTimeStr(v) })}
                        ampm={ampm}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                    {event.type === 'weekly' && (
                        <Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                {t('deviceSchedule.daysOfWeek')}
                            </Typography>
                            <ToggleButtonGroup
                                value={event.days_of_week}
                                onChange={(_, val) => update({ days_of_week: val })}
                                size="small"
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {DAYS.map(d => (
                                    <ToggleButton key={d} value={d} sx={DAY_TOGGLE_SX}>
                                        {t(`deviceSchedule.days.${d}`)}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    )}
                    {event.type === 'one_time' && (
                        <DatePicker
                            label={t('deviceSchedule.date')}
                            value={event.date ? dayjs(event.date) : null}
                            onChange={v => update({ date: v ? v.format('YYYY-MM-DD') : null })}
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                    )}
                </>
            )}

            <Divider />

            {compact ? (
                /* Compact: images and TriStates in two columns */
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {boardPickers}
                    {triStates}
                </Box>
            ) : (
                /* Normal: images then TriStates stacked */
                <>
                    {boardPickers}
                    {triStates}
                </>
            )}
        </Box>
    );
}

function DeviceSchedule({ deviceID, device, schedule, onScheduleChange }) {
    const { t, i18n } = useTranslation();
    const [expanded, setExpanded] = React.useState(false);
    const [hovering, setHovering] = React.useState(false);
    const [draft, setDraft] = React.useState(() => scheduleToInternal(schedule));
    const [isDirty, setIsDirty] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [company, setCompany] = React.useState(null);
    const prevScheduleRef = React.useRef(schedule);

    const ampm = uses12h(i18n.language);
    const boards = [...new Map((device?.components || []).map(b => [b.position, b])).values()];

    React.useEffect(() => {
        fetchAuthSession().then(session => {
            const payload = session.tokens?.idToken?.payload;
            setCompany(payload?.['custom:company'] ?? null);
        }).catch(() => {});
    }, []);

    React.useEffect(() => {
        getDeviceSchedule(deviceID).then(fetched => {
            if (fetched) {
                setDraft(scheduleToInternal(fetched));
                onScheduleChange?.(fetched);
            }
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceID]);

    React.useEffect(() => {
        if (!isDirty && schedule !== prevScheduleRef.current) {
            prevScheduleRef.current = schedule;
            setDraft(scheduleToInternal(schedule));
        }
    }, [schedule, isDirty]);

    function updateEvent(id, updated) {
        setDraft(d => ({ ...d, events: d.events.map(e => e.id === id ? updated : e) }));
        setIsDirty(true);
        setError(null);
    }

    function deleteEvent(id) {
        setDraft(d => ({ ...d, events: d.events.filter(e => e.id !== id) }));
        setIsDirty(true);
        setError(null);
    }

    function addEvent() {
        setDraft(d => ({ ...d, events: [...d.events, makeDefaultEvent()] }));
        setIsDirty(true);
        setError(null);
    }

    async function handleSave() {
        setSaving(true);
        setError(null);
        try {
            const sorted = [...draft.events].sort((a, b) => nextOccurrenceMs(a) - nextOccurrenceMs(b));
            const toSave = {
                timezone: draft.timezone,
                events: sorted.map(({ id, ...rest }) => rest),
            };
            await setDeviceSchedule(deviceID, toSave);
            setDraft(d => ({ ...d, events: sorted }));
            setIsDirty(false);
            setHovering(false);
            onScheduleChange?.(toSave);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        setSaving(true);
        setError(null);
        try {
            await deleteDeviceSchedule(deviceID);
            setDraft({ timezone: defaultTZ(), events: [] });
            setIsDirty(false);
            onScheduleChange?.(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    }

    const count = draft.events.length;

    const summary = count === 0
        ? t('deviceSchedule.noEvents')
        : t('deviceSchedule.eventCount', { count });

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language.split('-')[0]}>
            <Box sx={{ mt: 1 }}>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        {t('deviceSchedule.title')}
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            {summary}
                        </Typography>
                    </Typography>
                    <IconButton size="small" onClick={() => setHovering(true)} aria-label="open schedule view">
                        <OpenInFullIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setExpanded(e => !e)} aria-label="toggle schedule">
                        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                </Box>

                <Dialog
                    open={hovering}
                    onClose={() => setHovering(false)}
                    maxWidth={false}
                    PaperProps={{ sx: { m: 3, maxWidth: 'calc(100vw - 48px)' } }}
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        {t('deviceSchedule.title')}
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                            {deviceID}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pb: 1, pt: '20px !important' }}>
                        <Box sx={{ mb: 1.5 }}>
                            <Autocomplete
                                options={TIMEZONES}
                                value={draft.timezone}
                                onChange={(_, val) => {
                                    if (val) { setDraft(d => ({ ...d, timezone: val })); setIsDirty(true); }
                                }}
                                renderInput={params => (
                                    <TextField {...params} label={t('deviceSchedule.timezone')} size="small" />
                                )}
                                sx={{ maxWidth: 320 }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                            {draft.events.map(event => (
                                <Box key={event.id} sx={{ flexShrink: 0, width: 280 }}>
                                    <EventEditor
                                        event={event}
                                        boards={boards}
                                        company={company}
                                        deviceTypeID={device?.dtid}
                                        ampm={ampm}
                                        onChange={updated => updateEvent(event.id, updated)}
                                        onDelete={() => deleteEvent(event.id)}
                                    />
                                </Box>
                            ))}
                            {count < 6 && (
                                <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                                    <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addEvent}>
                                        {t('deviceSchedule.addEvent')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        {count >= 6 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {t('deviceSchedule.maxEvents')}
                            </Typography>
                        )}
                        {error && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{error}</Alert>}
                    </DialogContent>
                    <DialogActions>
                        {schedule && (
                            <Button variant="outlined" size="small" color="error" onClick={handleDelete} disabled={saving}>
                                {t('common.delete')}
                            </Button>
                        )}
                        <Button onClick={() => setHovering(false)} size="small">{t('common.close')}</Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={saving || !isDirty}
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        >
                            {saving ? t('common.saving') : t('common.save')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Collapse in={expanded}>
                    <Box sx={{ pt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Autocomplete
                            options={TIMEZONES}
                            value={draft.timezone}
                            onChange={(_, val) => {
                                if (val) {
                                    setDraft(d => ({ ...d, timezone: val }));
                                    setIsDirty(true);
                                }
                            }}
                            renderInput={params => (
                                <TextField {...params} label={t('deviceSchedule.timezone')} size="small" />
                            )}
                        />

                        {draft.events.map(event => (
                            <EventEditor
                                key={event.id}
                                event={event}
                                boards={boards}
                                company={company}
                                deviceTypeID={device?.dtid}
                                ampm={ampm}
                                onChange={updated => updateEvent(event.id, updated)}
                                onDelete={() => deleteEvent(event.id)}
                                compact
                            />
                        ))}

                        {count < 6 ? (
                            <Button
                                startIcon={<AddIcon />}
                                size="small"
                                variant="outlined"
                                onClick={addEvent}
                            >
                                {t('deviceSchedule.addEvent')}
                            </Button>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                {t('deviceSchedule.maxEvents')}
                            </Typography>
                        )}

                        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSave}
                                disabled={saving || !isDirty}
                                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                            >
                                {saving ? t('common.saving') : t('common.save')}
                            </Button>
                            {schedule && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    onClick={handleDelete}
                                    disabled={saving}
                                >
                                    {t('common.delete')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        </LocalizationProvider>
    );
}

export default DeviceSchedule;
